import { useMemo } from 'react';
import {
  AssistantRuntimeProvider,
  MessagePrimitive,
  ThreadPrimitive,
  useExternalStoreRuntime,
  type ThreadMessageLike,
} from '@assistant-ui/react';

import type { DataItem } from '../../../../../utils/interface-types';
import {
  grigoChatHistoryHasDisplayableMessages,
  mapGrigoChatHistoryToExternalMessages,
  type GrigoExternalChatMessage,
} from '../../lib/grigo-chat-external-messages';
import './grigo-assistant-ui-thread-poc.css';

export type GrigoAssistantUiThreadPocProps = {
  chatHistory: DataItem[];
  isRunning: boolean;
};

const convertMessage = (message: GrigoExternalChatMessage): ThreadMessageLike => ({
  id: message.id,
  role: message.role,
  content: [{ type: 'text', text: message.content }],
});

function GrigoAssistantUiThreadViewport({
  chatHistory,
  isRunning,
}: GrigoAssistantUiThreadPocProps) {
  const externalMessages = useMemo(
    () => mapGrigoChatHistoryToExternalMessages(chatHistory),
    [chatHistory],
  );

  const runtime = useExternalStoreRuntime({
    messages: externalMessages,
    convertMessage,
    isRunning,
    onNew: async () => {
      /* Read-only POC: Grigo query-container owns send/stream. */
    },
  });

  if (!grigoChatHistoryHasDisplayableMessages(chatHistory)) {
    return (
      <p className="grigo-assistant-ui-poc-empty text-xs text-gray-500 px-3 py-2">
        Send a message to preview assistant-ui thread mapping.
      </p>
    );
  }

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ThreadPrimitive.Root className="grigo-assistant-ui-thread">
        <ThreadPrimitive.Viewport className="grigo-assistant-ui-thread-viewport">
          <ThreadPrimitive.Messages
            components={{
              UserMessage: () => (
                <MessagePrimitive.Root className="grigo-assistant-ui-msg grigo-assistant-ui-msg-user">
                  <MessagePrimitive.Content />
                </MessagePrimitive.Root>
              ),
              AssistantMessage: () => (
                <MessagePrimitive.Root className="grigo-assistant-ui-msg grigo-assistant-ui-msg-assistant">
                  <MessagePrimitive.Content />
                </MessagePrimitive.Root>
              ),
            }}
          />
          {isRunning ? (
            <div className="grigo-assistant-ui-poc-running text-xs text-gray-500 px-2 py-1">
              Streaming…
            </div>
          ) : null}
        </ThreadPrimitive.Viewport>
      </ThreadPrimitive.Root>
    </AssistantRuntimeProvider>
  );
}

/**
 * Read-only assistant-ui ExternalStoreRuntime POC — mirrors Grigo SSE chat state.
 * Enable with REACT_APP_CHAT_ASSISTANT_UI_POC=true.
 */
function GrigoAssistantUiThreadPoc({ chatHistory, isRunning }: GrigoAssistantUiThreadPocProps) {
  return (
    <aside
      className="grigo-assistant-ui-poc-panel border border-dashed border-[#0A8BC7]/40 rounded-lg bg-white/90 m-2 max-w-3xl mx-auto"
      aria-label="assistant-ui thread proof of concept"
    >
      <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold text-[#0A8BC7] uppercase tracking-wide">
          assistant-ui POC
        </span>
        <span className="text-[10px] text-gray-500">ExternalStoreRuntime · read-only</span>
      </div>
      <GrigoAssistantUiThreadViewport chatHistory={chatHistory} isRunning={isRunning} />
    </aside>
  );
}

export default GrigoAssistantUiThreadPoc;
